import { useState } from "react";
import type { MouseEvent } from "react";
import {
  Badge,
  Box,
  Button,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { NotificationsNone as BellIcon } from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { timeAgo } from "src/reusable/Functions";
import {
  useNotificationEffects,
  useNotificationPrefs,
  useNotificationStreamHealth,
  useSeenCursorSync,
  useTabBadge,
  useUserNotificationStream,
} from "../hooks";
import { clearNotificationHistory } from "../api";
import { desktopSupport, testNotificationAlerts } from "../effects";
import { presentNotification } from "../registry";
import { useUserNotificationStore } from "../store";
import "../kinds";

const DESKTOP_STATUS_COPY: Record<string, string> = {
  denied:
    "Desktop notifications are blocked for this site — allow them via the lock icon in the address bar.",
  unsupported: "This browser doesn't support desktop notifications.",
  insecure:
    "Desktop notifications need a secure (https) connection to this site.",
  default: "Click Test alerts to grant desktop notification permission.",
};

interface NotificationsButtonProps {
  color?: "inherit" | "default" | "primary" | "secondary";
}

/** Header bell: owns the user's stream subscription and lists what arrived. */
const NotificationsButton = ({
  color = "inherit",
}: NotificationsButtonProps) => {
  useUserNotificationStream();
  useNotificationEffects();
  useSeenCursorSync();
  useTabBadge();
  const prefs = useNotificationPrefs();
  const streamHealthy = useNotificationStreamHealth();
  const history = useHistory();
  const notifications = useUserNotificationStore((s) => s.notifications);
  const unreadCount = useUserNotificationStore((s) => s.unreadCount);
  const markAllRead = useUserNotificationStore((s) => s.markAllRead);
  const markRead = useUserNotificationStore((s) => s.markRead);
  const clearAll = useUserNotificationStore((s) => s.clearAll);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const close = () => setAnchorEl(null);
  const onClearAll = () => {
    clearAll();
    void clearNotificationHistory();
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          data-testid="hdr-btn-notifications"
          onClick={open}
          color={color}
          size="small"
          aria-label="Notifications"
        >
          <Badge
            color="error"
            badgeContent={unreadCount}
            max={99}
            overlap="circular"
          >
            <BellIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 360, maxHeight: 480 } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            Notifications
          </Typography>
          <Tooltip
            title={
              prefs.desktop && desktopSupport() === "denied"
                ? "Desktop notifications are blocked for this site in your browser settings"
                : "Play the enabled alerts (sound, pop-up, desktop) once"
            }
          >
            <Button
              size="small"
              data-testid="notifications-test-alerts"
              onClick={() => void testNotificationAlerts(prefs)}
            >
              Test alerts
            </Button>
          </Tooltip>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={markAllRead}
              data-testid="notifications-mark-all-read"
            >
              Mark all read
            </Button>
          )}
          {notifications.length > 0 && (
            <Button
              size="small"
              onClick={onClearAll}
              data-testid="notifications-clear-all"
            >
              Clear all
            </Button>
          )}
        </Box>
        {prefs.desktop && desktopSupport() !== "granted" && (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ px: 2, pb: 1, display: "block" }}
            data-testid="notifications-desktop-status"
          >
            {DESKTOP_STATUS_COPY[desktopSupport()]}
          </Typography>
        )}
        {!streamHealthy && (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ px: 2, pb: 1, display: "block" }}
            data-testid="notifications-stream-paused"
          >
            Live updates are paused — reconnecting…
          </Typography>
        )}
        {notifications.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 2, py: 2 }}
            data-testid="notifications-empty"
          >
            {streamHealthy
              ? "You're all caught up."
              : "Nothing new since the connection dropped."}
          </Typography>
        )}
        {notifications.map((n) => {
          const { title, message, link } = presentNotification(n);
          return (
            <MenuItem
              key={n.id}
              data-testid={`notification-${n.id}`}
              data-unread={!n.read || undefined}
              onClick={() => {
                markRead(n.id);
                close();
                if (link) history.push(link);
              }}
              sx={{
                whiteSpace: "normal",
                alignItems: "flex-start",
                bgcolor: n.read ? undefined : "action.hover",
              }}
            >
              <Box
                component="span"
                aria-label={n.read ? undefined : "Unread"}
                sx={{
                  width: 8,
                  height: 8,
                  mt: 1,
                  mr: 1.5,
                  borderRadius: "50%",
                  flexShrink: 0,
                  bgcolor: n.read ? "transparent" : "primary.main",
                }}
              />
              <ListItemText
                primary={title}
                primaryTypographyProps={{
                  fontWeight: n.read ? "normal" : 600,
                }}
                secondary={
                  <>
                    {message && (
                      <Typography
                        component="span"
                        variant="body2"
                        display="block"
                      >
                        {message}
                      </Typography>
                    )}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      {timeAgo(n.created_at)}
                    </Typography>
                  </>
                }
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default NotificationsButton;
